<template>
  <div class="booking-view-page pa-4 pa-md-6">
    <div class="page-container">
      <!-- Breadcrumb & Top Bar -->
    <div class="d-flex flex-wrap align-center justify-space-between mb-6">
      <div class="d-flex align-center">
        <v-btn
          icon
          class="me-2"
          color="#082C69"
          @click="$router.push('/customer-service/bookings')"
        >
          <v-icon size="24">mdi-arrow-right</v-icon>
        </v-btn>
        <div>
          <div class="d-flex align-center gap-1 text-caption text-muted mb-1">
            <span class="breadcrumb-link" @click="$router.push('/customer-service')">الرئيسية</span>
            <v-icon x-small color="#94A3B8">mdi-chevron-left</v-icon>
            <span class="breadcrumb-link" @click="$router.push('/customer-service/bookings')">الحجوزات</span>
            <v-icon x-small color="#94A3B8">mdi-chevron-left</v-icon>
            <span class="navy--text font-weight-medium">تفاصيل الحجز</span>
          </div>
          <h1 class="text-h5 font-weight-bold navy--text mb-0 d-flex align-center">
            <span>الحجز</span>
            <span class="mono-code ms-2">{{ booking.booking_ref || '...' }}</span>
          </h1>
        </div>
      </div>

      <!-- Top Actions -->
      <div class="d-flex align-center gap-2">
        <v-btn
          outlined
          color="#64748B"
          class="rounded-lg font-weight-bold"
          @click="$router.push('/customer-service/bookings')"
        >
          <v-icon left size="18">mdi-arrow-left</v-icon>
          رجوع
        </v-btn>

        <!-- View Customer Button -->
        <v-btn
          v-if="booking.customer"
          outlined
          color="#082C69"
          class="rounded-lg font-weight-bold"
          @click="goToCustomer"
        >
          <v-icon left size="18">mdi-account-outline</v-icon>
          ملف العميل
        </v-btn>

        <!-- Refund Request Button (Phase Notice) -->
        <v-btn
          outlined
          color="#8B5CF6"
          class="rounded-lg font-weight-bold"
          @click="showRefundNoticeDialog = true"
        >
          <v-icon left size="18">mdi-cash-refund</v-icon>
          إنشاء طلب استرداد
        </v-btn>

        <!-- Cancel Booking Button -->
        <v-tooltip bottom :disabled="booking.is_cancellable">
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-btn
                color="#EF4444"
                class="white--text rounded-lg font-weight-bold"
                elevation="1"
                :disabled="!booking.is_cancellable"
                @click="openCancelDialog"
              >
                <v-icon left size="18">mdi-cancel</v-icon>
                إلغاء الحجز
              </v-btn>
            </span>
          </template>
          <span>{{ booking.cancellation_block_reason || 'غير متاح للإلغاء' }}</span>
        </v-tooltip>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="#082C69" size="48" class="mb-3"></v-progress-circular>
      <div class="text-subtitle-1 text-muted">جاري تحميل تفاصيل الحجز...</div>
    </div>

    <!-- Error / Not Found States Differentiated by HTTP Status -->
    <div v-else-if="!booking.id" class="text-center py-12">
      <v-icon size="64" :color="errorStatus && errorStatus >= 500 ? '#EF4444' : '#CBD5E1'" class="mb-3">
        {{ errorStatus === 403 ? 'mdi-shield-lock-outline' : 'mdi-alert-circle-outline' }}
      </v-icon>
      <div class="text-h6 font-weight-bold navy--text mb-1">{{ errorTitle }}</div>
      <div class="text-body-2 text-muted mb-4">{{ errorMessage }}</div>
      <div class="d-flex justify-center gap-2">
        <v-btn
          v-if="errorStatus !== 404 && errorStatus !== 403 && errorStatus !== 422"
          outlined
          color="#082C69"
          class="rounded-lg font-weight-bold"
          @click="fetchBookingDetails"
        >
          <v-icon left size="16">mdi-refresh</v-icon>
          إعادة المحاولة
        </v-btn>
        <v-btn color="#082C69" class="white--text rounded-lg font-weight-bold" @click="$router.push('/customer-service/bookings')">
          العودة لقائمة الحجوزات
        </v-btn>
      </div>
    </div>

    <div v-else>
      <!-- Wide Summary Strip Card -->
      <v-card class="elevation-1 rounded-xl pa-5 mb-6 summary-card">
        <v-row align="center" dense>
          <!-- Booking Ref & UUID -->
          <v-col cols="12" sm="6" md="3" class="summary-col">
            <div class="text-caption text-muted font-weight-medium">رقم الحجز</div>
            <div class="mono-code text-h6 font-weight-bold navy--text mb-1">
              {{ booking.booking_ref }}
            </div>
            <div v-if="booking.booking_group_id" class="d-flex align-center">
              <span class="text-caption text-muted mono-code me-1" style="font-size: 0.75rem;">
                UUID: {{ booking.booking_group_id.substring(0, 8) }}...
              </span>
              <v-btn icon x-small @click="copyUuid">
                <v-icon size="14" color="#64748B">mdi-content-copy</v-icon>
              </v-btn>
            </div>
          </v-col>

          <!-- Booking Date -->
          <v-col cols="12" sm="6" md="2" class="summary-col">
            <div class="text-caption text-muted font-weight-medium">تاريخ الحجز</div>
            <div class="text-body-1 font-weight-bold navy--text">
              {{ booking.reservation_date }}
            </div>
            <div class="text-caption text-muted">{{ booking.created_at }}</div>
          </v-col>

          <!-- Status -->
          <v-col cols="12" sm="6" md="2" class="summary-col text-center">
            <div class="text-caption text-muted font-weight-medium mb-1">حالة الحجز</div>
            <v-chip
              class="font-weight-bold px-4"
              :class="getStatusChipClass(booking.status_code)"
            >
              <span class="cs-chip-text">{{ booking.status_label }}</span>
            </v-chip>
          </v-col>

          <!-- Payment Method -->
          <v-col cols="12" sm="6" md="2" class="summary-col text-center">
            <div class="text-caption text-muted font-weight-medium mb-1">طريقة الدفع</div>
            <v-chip
              class="font-weight-bold px-4"
              :class="getPaymentMethodChipClass(booking.payment_method)"
            >
              <span class="cs-chip-text">{{ booking.payment_method_label }}</span>
            </v-chip>
          </v-col>

          <!-- Total Amount & Seats -->
          <v-col cols="12" sm="6" md="3" class="summary-col text-left">
            <div class="text-caption text-muted font-weight-medium">إجمالي الحجز</div>
            <div class="text-h5 font-weight-bold navy--text">
              {{ booking.total_amount }} <span class="text-body-2 text-muted">ج.م</span>
            </div>
            <div class="text-caption text-muted font-weight-bold">
              {{ booking.seats_count }} مقاعد محجوزة
            </div>
          </v-col>
        </v-row>
      </v-card>

      <!-- Row 1: Customer Details, Trip/Route Details, Stops -->
      <v-row class="mb-6">
        <!-- Card 1: Customer Details -->
        <v-col cols="12" md="4">
          <v-card class="elevation-1 rounded-xl pa-5 fill-height detail-card">
            <div class="d-flex align-center justify-space-between mb-4 border-bottom pb-3">
              <div class="d-flex align-center">
                <v-icon color="#082C69" size="20" class="me-2">mdi-account-outline</v-icon>
                <span class="text-subtitle-1 font-weight-bold navy--text">بيانات العميل</span>
              </div>
              <v-chip
                v-if="booking.customer"
                x-small
                class="font-weight-bold"
                :class="booking.customer.status_id === 1 ? 'cs-status-chip-confirmed' : 'cs-status-chip-cancelled'"
              >
                <span class="cs-chip-text">{{ booking.customer.status_label }}</span>
              </v-chip>
            </div>

            <div v-if="booking.customer" class="customer-info-box">
              <div class="d-flex align-center mb-4">
                <div class="customer-avatar me-3">
                  {{ getInitials(booking.customer.name) }}
                </div>
                <div>
                  <div class="text-subtitle-1 font-weight-bold navy--text">{{ booking.customer.name }}</div>
                  <div class="text-caption text-muted mono-code">{{ booking.customer.customer_ref }}</div>
                </div>
              </div>

              <div class="info-row d-flex align-center justify-space-between py-2 border-bottom-dashed">
                <span class="text-caption text-muted">رقم الهاتف</span>
                <span class="text-body-2 font-weight-bold navy--text dir-ltr">{{ booking.customer.phone }}</span>
              </div>

              <div class="info-row d-flex align-center justify-space-between py-2 border-bottom-dashed">
                <span class="text-caption text-muted">البريد الإلكتروني</span>
                <span class="text-body-2 font-weight-medium text-truncate" style="max-width: 180px;">
                  {{ booking.customer.email }}
                </span>
              </div>

              <div class="mt-4 pt-2">
                <v-btn
                  block
                  outlined
                  color="#082C69"
                  class="rounded-lg font-weight-bold text-caption"
                  @click="goToCustomer"
                >
                  <v-icon left size="16">mdi-open-in-new</v-icon>
                  فتح ملف العميل الكامل
                </v-btn>
                <v-btn
                  block
                  outlined
                  color="#082C69"
                  class="rounded-lg font-weight-bold text-caption mt-2"
                  :to="`/customer-service/tickets?search=${booking.booking_ref || booking.id}`"
                >
                  <v-icon left size="16">mdi-ticket-account</v-icon>
                  التذاكر والشكاوى المرتبطة
                </v-btn>
              </div>
            </div>
            <div v-else class="text-muted text-caption py-4">
              بيانات العميل غير متوفرة
            </div>
          </v-card>
        </v-col>

        <!-- Card 2: Trip & Route Details -->
        <v-col cols="12" md="4">
          <v-card class="elevation-1 rounded-xl pa-5 fill-height detail-card">
            <div class="d-flex align-center justify-space-between mb-4 border-bottom pb-3">
              <div class="d-flex align-center">
                <v-icon color="#082C69" size="20" class="me-2">mdi-bus-clock</v-icon>
                <span class="text-subtitle-1 font-weight-bold navy--text">الرحلة والمسار</span>
              </div>
              <v-chip x-small class="font-weight-bold mono-code cs-trip-chip">
                {{ booking.trip ? booking.trip.trip_ref : '—' }}
              </v-chip>
            </div>

            <div v-if="booking.trip" class="trip-info-box">
              <div class="info-row d-flex align-center justify-space-between py-2 border-bottom-dashed">
                <span class="text-caption text-muted">المسار</span>
                <span class="text-body-2 font-weight-bold navy--text">
                  {{ booking.trip.route ? booking.trip.route.name : '—' }}
                </span>
              </div>

              <div class="info-row d-flex align-center justify-space-between py-2 border-bottom-dashed">
                <span class="text-caption text-muted">تاريخ الرحلة</span>
                <span class="text-body-2 font-weight-bold navy--text">{{ booking.trip.planned_date }}</span>
              </div>

              <div class="info-row d-flex align-center justify-space-between py-2 border-bottom-dashed">
                <span class="text-caption text-muted">وقت الانطلاق</span>
                <span class="text-body-2 font-weight-bold navy--text">{{ booking.departure_time }}</span>
              </div>

              <div class="info-row d-flex align-center justify-space-between py-2 border-bottom-dashed">
                <span class="text-caption text-muted">السائق المعين</span>
                <span class="text-body-2 font-weight-medium navy--text">
                  {{ booking.trip.driver ? booking.trip.driver.name : 'لم يتم تعيين سائق' }}
                </span>
              </div>

              <div class="info-row d-flex align-center justify-space-between py-2">
                <span class="text-caption text-muted">الحافلة</span>
                <span class="text-body-2 font-weight-medium navy--text">
                  {{ booking.trip.bus ? booking.trip.bus.plate_number : 'لم يتم تعيين حافلة' }}
                </span>
              </div>
            </div>
            <div v-else class="text-muted text-caption py-4">
              بيانات الرحلة غير متوفرة
            </div>
          </v-card>
        </v-col>

        <!-- Card 3: Boarding & Alighting Stops -->
        <v-col cols="12" md="4">
          <v-card class="elevation-1 rounded-xl pa-5 fill-height detail-card">
            <div class="d-flex align-center justify-space-between mb-4 border-bottom pb-3">
              <div class="d-flex align-center">
                <v-icon color="#082C69" size="20" class="me-2">mdi-map-marker-path</v-icon>
                <span class="text-subtitle-1 font-weight-bold navy--text">نقاط الصعود والنزول</span>
              </div>
            </div>

            <div class="stops-timeline py-2">
              <!-- Boarding Stop -->
              <div class="stop-item d-flex align-start mb-4">
                <div class="stop-icon-circle bg-green-light me-3 mt-1">
                  <v-icon size="16" color="#10B981">mdi-ray-start-arrow</v-icon>
                </div>
                <div>
                  <div class="text-caption text-muted font-weight-medium">محطة الصعود</div>
                  <div class="text-body-2 font-weight-bold navy--text">
                    {{ booking.stops ? booking.stops.start_stop : '—' }}
                  </div>
                </div>
              </div>

              <div class="stop-line-divider me-5 mb-2"></div>

              <!-- Alighting Stop -->
              <div class="stop-item d-flex align-start">
                <div class="stop-icon-circle bg-red-light me-3 mt-1">
                  <v-icon size="16" color="#EF4444">mdi-ray-end</v-icon>
                </div>
                <div>
                  <div class="text-caption text-muted font-weight-medium">محطة النزول</div>
                  <div class="text-body-2 font-weight-bold navy--text">
                    {{ booking.stops ? booking.stops.end_stop : '—' }}
                  </div>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Row 2: Seats Breakdown Table & Financial/Refund Details -->
      <v-row class="mb-6">
        <!-- Seats Breakdown (Cols 7) -->
        <v-col cols="12" md="7">
          <v-card class="elevation-1 rounded-xl pa-5 fill-height detail-card">
            <div class="d-flex align-center justify-space-between mb-4 border-bottom pb-3">
              <div class="d-flex align-center">
                <v-icon color="#082C69" size="20" class="me-2">mdi-car-seat</v-icon>
                <span class="text-subtitle-1 font-weight-bold navy--text">المقاعد المحجوزة</span>
              </div>
              <v-chip x-small class="font-weight-bold cs-seats-chip">
                {{ booking.seats ? booking.seats.length : 0 }} مقاعد
              </v-chip>
            </div>

            <v-simple-table class="custom-seats-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-right">رقم المقعد</th>
                    <th class="text-right">رقم التذكرة</th>
                    <th class="text-right">سعر المقعد</th>
                    <th class="text-center">حالة الصعود</th>
                    <th class="text-right">ملاحظات / توقيت</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="seat in booking.seats" :key="seat.id">
                    <td>
                      <span class="font-weight-bold navy--text">{{ seat.seat_number }}</span>
                    </td>
                    <td>
                      <span class="mono-code text-caption font-weight-bold text-muted">{{ seat.ticket_number }}</span>
                    </td>
                    <td>
                      <span class="font-weight-bold navy--text">{{ seat.paid_price }} ج.م</span>
                    </td>
                    <td class="text-center">
                      <v-chip
                        x-small
                        class="font-weight-bold"
                        :class="getBoardingChipClass(seat.boarding_status)"
                      >
                        <span class="cs-chip-text">{{ seat.boarding_status_label }}</span>
                      </v-chip>
                    </td>
                    <td>
                      <span v-if="seat.boarded_at" class="text-caption text-green font-weight-bold">
                        صعد في {{ seat.boarded_at }}
                      </span>
                      <span v-else-if="seat.no_show_at" class="text-caption text-red font-weight-bold">
                        لم يحضر {{ seat.no_show_reason ? '(' + seat.no_show_reason + ')' : '' }}
                      </span>
                      <span v-else class="text-caption text-muted">—</span>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-col>

        <!-- Financial, Cash & Refund Details (Cols 5) -->
        <v-col cols="12" md="5">
          <v-card class="elevation-1 rounded-xl pa-5 fill-height detail-card">
            <div class="d-flex align-center justify-space-between mb-4 border-bottom pb-3">
              <div class="d-flex align-center">
                <v-icon color="#082C69" size="20" class="me-2">mdi-credit-card-outline</v-icon>
                <span class="text-subtitle-1 font-weight-bold navy--text">البيانات المالية والتحصيل</span>
              </div>
            </div>

            <!-- Financial Summary -->
            <div class="financial-box mb-4">
              <div class="info-row d-flex align-center justify-space-between py-2 border-bottom-dashed">
                <span class="text-caption text-muted">إجمالي المبلغ المطلوب</span>
                <span class="text-body-2 font-weight-bold navy--text">{{ booking.total_amount }} ج.م</span>
              </div>

              <div class="info-row d-flex align-center justify-space-between py-2 border-bottom-dashed">
                <span class="text-caption text-muted">وسيلة الدفع</span>
                <span class="text-body-2 font-weight-bold navy--text">{{ booking.payment_method_label }}</span>
              </div>
            </div>

            <!-- Cash Collection Section (If Cash) -->
            <div v-if="booking.cash_collection && booking.cash_collection.is_cash" class="cash-box mb-4 pa-3 rounded-lg bg-cash-box">
              <div class="d-flex align-center justify-space-between mb-2">
                <span class="text-caption font-weight-bold navy--text">تحصيل النقدية (الكاش)</span>
                <v-chip
                  x-small
                  class="font-weight-bold"
                  :class="booking.cash_collection.is_collected ? 'cs-status-chip-confirmed' : 'cs-status-chip-cancelled'"
                >
                  <span class="cs-chip-text">{{ booking.cash_collection.is_collected ? 'تم التحصيل' : 'لم يحصل بعد' }}</span>
                </v-chip>
              </div>

              <div v-if="booking.cash_collection.is_collected">
                <div class="text-caption text-muted">المبلغ المحصل: {{ booking.cash_collection.amount }} ج.م</div>
                <div class="text-caption text-muted">تاريخ التحصيل: {{ booking.cash_collection.collected_at }}</div>
                <div v-if="booking.cash_collection.collected_by_driver" class="text-caption text-muted">
                  تم بواسطة السائق: {{ booking.cash_collection.collected_by_driver.name }}
                </div>
              </div>
            </div>

            <!-- Linked Refund Request Section (If Any) -->
            <div v-if="booking.refund_request" class="mb-4 pa-3 rounded-lg" style="background-color: #F5F3FF; border: 1px solid #DDD6FE;">
              <div class="d-flex align-center justify-space-between mb-1">
                <span class="text-caption font-weight-bold text-purple">طلب استرداد مرتبط</span>
                <v-chip x-small color="#EDE9FE" text-color="#6D28D9" class="font-weight-bold">
                  {{ booking.refund_request.status_label }}
                </v-chip>
              </div>
              <div class="d-flex align-center justify-space-between mt-1">
                <span class="text-caption font-weight-bold mono-code navy--text">{{ booking.refund_request.request_code }}</span>
                <span class="text-caption font-weight-bold navy--text">{{ booking.refund_request.requested_amount }} ج.م</span>
              </div>
            </div>

            <!-- Cancellation & Refund Section -->
            <div v-if="booking.cancellation && booking.cancellation.is_cancelled" class="cancel-box pa-3 rounded-lg bg-cancel-box">
              <div class="d-flex align-center justify-space-between mb-2">
                <span class="text-caption font-weight-bold text-red">حالة الإلغاء</span>
                <v-chip x-small class="font-weight-bold cs-status-chip-cancelled">
                  <span class="cs-chip-text">ملغي</span>
                </v-chip>
              </div>
              <div class="text-caption text-muted mb-1">
                تاريخ الإلغاء: {{ booking.cancellation.cancelled_at || '—' }}
              </div>
              <div v-if="booking.cancellation.reason" class="text-caption text-muted mb-2">
                السبب: {{ booking.cancellation.reason }}
              </div>

              <!-- Refund Records -->
              <div v-if="booking.refund && booking.refund.has_refund" class="mt-2 pt-2 border-top-light">
                <div class="d-flex align-center justify-space-between">
                  <span class="text-caption font-weight-bold text-green">المبلغ المسترد للمحفظة</span>
                  <span class="text-body-2 font-weight-bold text-green">{{ booking.refund.total_refunded }} ج.م</span>
                </div>
              </div>
              <div v-else-if="booking.payment_method === 0" class="mt-2 pt-2 border-top-light">
                <span class="text-caption text-amber font-weight-bold">
                  لم يتم تسجيل عملية استرداد مالي بعد.
                </span>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Cancellation Confirmation Dialog -->
    <v-dialog v-model="cancelDialog" max-width="500px">
      <v-card class="rounded-xl pa-5">
        <div class="d-flex align-center mb-3">
          <div class="dialog-icon-circle bg-red-light me-3">
            <v-icon color="#EF4444" size="24">mdi-alert-outline</v-icon>
          </div>
          <div>
            <div class="text-h6 font-weight-bold navy--text">تأكيد إلغاء الحجز</div>
            <div class="text-caption text-muted">رقم الحجز: {{ booking.booking_ref }}</div>
          </div>
        </div>

        <v-alert
          dense
          outlined
          type="error"
          class="rounded-lg text-caption mb-4"
        >
          سيتم إلغاء جميع المقاعد المرتبطة بهذا الحجز ({{ booking.seats_count }} مقاعد) وإعادة سعة المقاعد للرحلة المجدولة، مع إرجاع المبلغ إلى محفظة العميل تلقائيًا إن وجد.
        </v-alert>

        <v-textarea
          v-model="cancelReason"
          label="سبب الإلغاء (اختياري)"
          placeholder="مثال: طلب العميل إلغاء الرحلة لظرف طارئ"
          outlined
          dense
          rows="3"
          class="filter-field mb-2"
        ></v-textarea>

        <div class="d-flex justify-end gap-2 mt-4">
          <v-btn
            outlined
            color="#64748B"
            class="rounded-lg font-weight-bold"
            :disabled="cancelling"
            @click="cancelDialog = false"
          >
            تراجع
          </v-btn>
          <v-btn
            color="#EF4444"
            class="white--text rounded-lg font-weight-bold"
            elevation="1"
            :loading="cancelling"
            @click="confirmCancelBooking"
          >
            تأكيد إلغاء الحجز
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Refund Notice Dialog (Phase 03 preview) -->
    <v-dialog v-model="showRefundNoticeDialog" max-width="500px">
      <v-card class="rounded-xl pa-5">
        <div class="d-flex align-center mb-3">
          <div class="dialog-icon-circle bg-purple-light me-3">
            <v-icon color="#8B5CF6" size="24">mdi-cash-refund</v-icon>
          </div>
          <div>
            <div class="text-h6 font-weight-bold navy--text">إنشاء طلب استرداد</div>
            <div class="text-caption text-muted">إدارة طلبات استرداد التذاكر</div>
          </div>
        </div>

        <v-alert
          dense
          outlined
          type="info"
          class="rounded-lg text-caption mb-4"
        >
          سيتم تفعيل نظام طلبات الاسترداد المالي المباشر في المرحلة القادمة (مرحلة المبالغ المستردة). في الوقت الحالي، يتم الاسترداد المالي التلقائي للمحفظة فور إلغاء الحجوزات الإلكترونية المؤهلة.
        </v-alert>

        <div class="d-flex justify-end">
          <v-btn
            color="#082C69"
            class="white--text rounded-lg font-weight-bold px-6"
            @click="showRefundNoticeDialog = false"
          >
            حسنًا
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Notification Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      top
      timeout="4000"
      class="font-weight-bold"
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">إغلاق</v-btn>
      </template>
    </v-snackbar>
    </div>
  </div>
</template>

<script>
import axios from '@/axios';

export default {
  name: 'CustomerServiceBookingView',
  data() {
    return {
      loading: true,
      booking: {},
      errorStatus: null,
      errorTitle: 'الحجز غير موجود',
      errorMessage: 'لم يتم العثور على الحجز المطلوب في النظام.',
      cancelDialog: false,
      cancelling: false,
      cancelReason: '',
      showRefundNoticeDialog: false,
      snackbar: {
        show: false,
        text: '',
        color: 'success',
      },
    };
  },
  watch: {
    '$route.params.id'() {
      this.fetchBookingDetails();
    },
  },
  created() {
    this.fetchBookingDetails();
  },
  methods: {
    async fetchBookingDetails() {
      this.loading = true;
      this.errorStatus = null;
      this.booking = {};
      const bookingId = this.$route.params.id;
      try {
        const response = await axios.get(`/customer-service/bookings/${encodeURIComponent(bookingId)}`);
        const payload = response && response.data ? (response.data.data || response.data) : {};
        this.booking = payload || {};
      } catch (err) {
        console.error('Error fetching booking details:', err);
        const status = err && err.response ? err.response.status : 500;
        this.errorStatus = status;

        if (status === 404) {
          this.errorTitle = 'الحجز غير موجود';
          this.errorMessage = 'لم يتم العثور على الحجز المطلوب في النظام.';
          this.showSnackbar('الحجز المطلوب غير موجود.', 'warning');
        } else if (status === 403) {
          this.errorTitle = 'غير مصرح بعرض هذا الحجز';
          this.errorMessage = 'ليس لديك صلاحية للوصول إلى تفاصيل هذا الحجز.';
          this.showSnackbar('غير مصرح لك بعرض هذا الحجز.', 'error');
        } else if (status === 422) {
          this.errorTitle = 'معرف الحجز غير صالح';
          this.errorMessage = 'معرف الحجز المرسل غير صحيح. يرجى فتح الحجز من قائمة الحجوزات.';
          this.showSnackbar('معرف الحجز غير صالح.', 'warning');
        } else {
          this.errorTitle = 'تعذر تحميل بيانات الحجز';
          this.errorMessage = 'حدث خطأ غير متوقع أثناء جلب تفاصيل الحجز. يرجى المحاولة مرة أخرى.';
          this.showSnackbar('تعذر تحميل بيانات الحجز. يرجى المحاولة مرة أخرى.', 'error');
        }
      } finally {
        this.loading = false;
      }
    },
    goToCustomer() {
      if (this.booking.customer && this.booking.customer.id) {
        this.$router.push(`/customer-service/customers/${this.booking.customer.id}`);
      }
    },
    copyUuid() {
      if (this.booking.booking_group_id) {
        navigator.clipboard.writeText(this.booking.booking_group_id);
        this.showSnackbar('تم نسخ المعرف الفريد بنجاح.', 'success');
      }
    },
    openCancelDialog() {
      this.cancelReason = '';
      this.cancelDialog = true;
    },
    async confirmCancelBooking() {
      this.cancelling = true;
      try {
        const response = await axios.post(`/customer-service/bookings/${this.booking.id}/cancel`, {
          reason: this.cancelReason || 'إلغاء بواسطة خدمة العملاء',
        });
        this.cancelDialog = false;
        this.showSnackbar(response.data.message || 'تم إلغاء الحجز بنجاح.', 'success');
        this.fetchBookingDetails();
      } catch (err) {
        const msg = err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : 'تعذر إلغاء الحجز.';
        this.showSnackbar(msg, 'error');
      } finally {
        this.cancelling = false;
      }
    },
    showSnackbar(text, color = 'success') {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    getInitials(name) {
      if (!name) return '—';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    getStatusChipClass(code) {
      switch (code) {
        case 'confirmed':
          return 'cs-status-chip-confirmed';
        case 'in_progress':
          return 'cs-status-chip-in-progress';
        case 'completed':
          return 'cs-status-chip-completed';
        case 'cancelled':
          return 'cs-status-chip-cancelled';
        case 'no_show':
          return 'cs-status-chip-no-show';
        default:
          return 'cs-status-chip-default';
      }
    },
    getPaymentMethodChipClass(method) {
      switch (method) {
        case 1:
          return 'cs-pay-chip-cash';
        case 2:
          return 'cs-pay-chip-pass';
        default:
          return 'cs-pay-chip-wallet';
      }
    },
    getBoardingChipClass(status) {
      switch (status) {
        case 'boarded':
          return 'cs-status-chip-confirmed';
        case 'missed':
        case 'no_show':
          return 'cs-status-chip-cancelled';
        default:
          return 'cs-status-chip-no-show';
      }
    },
  },
};
</script>

<style scoped>
.booking-view-page {
  background-color: #F8FAFC;
  min-height: 100vh;
  font-family: 'Cairo', sans-serif !important;
}

.page-container {
  max-width: 1600px;
  margin: 0 auto;
}

.navy--text {
  color: #082C69 !important;
}

.text-muted {
  color: #64748B !important;
}

.text-green {
  color: #10B981 !important;
}

.text-red {
  color: #EF4444 !important;
}

.text-purple {
  color: #8B5CF6 !important;
}

.text-amber {
  color: #F59E0B !important;
}

.mono-code {
  font-family: 'Roboto Mono', monospace;
}

.dir-ltr {
  direction: ltr !important;
  display: inline-block;
}

.breadcrumb-link {
  cursor: pointer;
  transition: color 0.15s;
}

.breadcrumb-link:hover {
  color: #082C69 !important;
  text-decoration: underline;
}

.summary-card {
  border: 1px solid #E2E8F0 !important;
  background-color: #FFFFFF !important;
}

.summary-col {
  border-left: 1px solid #F1F5F9;
}

.summary-col:last-child {
  border-left: none;
}

.detail-card {
  border: 1px solid #E2E8F0 !important;
  background-color: #FFFFFF !important;
}

.border-bottom {
  border-bottom: 1px solid #E2E8F0;
}

.border-bottom-dashed {
  border-bottom: 1px dashed #F1F5F9;
}

.border-top-light {
  border-top: 1px solid #E2E8F0;
}

.customer-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background-color: #EEF2F6;
  color: #082C69;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.stop-icon-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stop-line-divider {
  width: 2px;
  height: 24px;
  background-color: #CBD5E1;
  margin-right: 13px;
}

.dialog-icon-circle {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bg-blue-light {
  background-color: #EEF2F6;
}

.bg-green-light {
  background-color: #ECFDF5;
}

.bg-red-light {
  background-color: #FEF2F2;
}

.bg-purple-light {
  background-color: #F5F3FF;
}

.bg-amber-light {
  background-color: #FFFBEB;
}

.bg-cash-box {
  background-color: #F8FAFC;
  border: 1px solid #E2E8F0;
}

.bg-cancel-box {
  background-color: #FEF2F2;
  border: 1px solid #FECACA;
}

.custom-seats-table ::v-deep th {
  background-color: #F8FAFC !important;
  color: #475569 !important;
  font-weight: 700 !important;
  font-size: 0.8rem !important;
  border-bottom: 1px solid #E2E8F0 !important;
  height: 42px !important;
}

.custom-seats-table ::v-deep td {
  border-bottom: 1px solid #F1F5F9 !important;
  height: 48px !important;
}

.gap-1 {
  gap: 4px;
}

.gap-2 {
  gap: 8px;
}

/* ========================================================
   HIGH CONTRAST CHIPS & BADGES
   ======================================================== */
.cs-status-chip-confirmed {
  background-color: #ECFDF5 !important;
  border: 1px solid #A7F3D0 !important;
}
.cs-status-chip-confirmed ::v-deep .v-chip__content,
.cs-status-chip-confirmed .cs-chip-text {
  color: #065F46 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-status-chip-in-progress {
  background-color: #EFF6FF !important;
  border: 1px solid #BFDBFE !important;
}
.cs-status-chip-in-progress ::v-deep .v-chip__content,
.cs-status-chip-in-progress .cs-chip-text {
  color: #1E40AF !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-status-chip-completed {
  background-color: #F5F3FF !important;
  border: 1px solid #DDD6FE !important;
}
.cs-status-chip-completed ::v-deep .v-chip__content,
.cs-status-chip-completed .cs-chip-text {
  color: #5B21B6 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-status-chip-cancelled {
  background-color: #FEF2F2 !important;
  border: 1px solid #FECACA !important;
}
.cs-status-chip-cancelled ::v-deep .v-chip__content,
.cs-status-chip-cancelled .cs-chip-text {
  color: #991B1B !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-status-chip-no-show {
  background-color: #FFFBEB !important;
  border: 1px solid #FDE68A !important;
}
.cs-status-chip-no-show ::v-deep .v-chip__content,
.cs-status-chip-no-show .cs-chip-text {
  color: #92400E !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-status-chip-default {
  background-color: #F1F5F9 !important;
  border: 1px solid #E2E8F0 !important;
}
.cs-status-chip-default ::v-deep .v-chip__content,
.cs-status-chip-default .cs-chip-text {
  color: #334155 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-pay-chip-wallet {
  background-color: #EEF2F6 !important;
  border: 1px solid #CBD5E1 !important;
}
.cs-pay-chip-wallet ::v-deep .v-chip__content,
.cs-pay-chip-wallet .cs-chip-text {
  color: #082C69 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-pay-chip-cash {
  background-color: #F0FDF4 !important;
  border: 1px solid #BBF7D0 !important;
}
.cs-pay-chip-cash ::v-deep .v-chip__content,
.cs-pay-chip-cash .cs-chip-text {
  color: #166534 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-pay-chip-pass {
  background-color: #FFF7ED !important;
  border: 1px solid #FFEDD5 !important;
}
.cs-pay-chip-pass ::v-deep .v-chip__content,
.cs-pay-chip-pass .cs-chip-text {
  color: #9A3412 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-trip-chip {
  background-color: #F1F5F9 !important;
  border: 1px solid #CBD5E1 !important;
  color: #1E293B !important;
  font-weight: 700 !important;
}

.cs-seats-chip {
  background-color: #EEF2F6 !important;
  border: 1px solid #CBD5E1 !important;
  color: #082C69 !important;
}
</style>
